import React, { useCallback, type SyntheticEvent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import MediaServicesZoomInIcon from '@atlaskit/icon/core/zoom-in';
import MediaServicesZoomOutIcon from '@atlaskit/icon/core/zoom-out';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Range from '@atlaskit/range';
import { useIntl, defineMessages } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	LINEAR_ZOOM_STEP,
	MAXIMUM_LINEAR_ZOOM,
	MINIMUM_LINEAR_ZOOM,
	getExponentialZoom,
	getLinearZoom,
	isMaximumZoom,
	isMinimumZoom,
} from './isMaximumZoom.tsx';

const messages = defineMessages({
	zoomIn: {
		id: 'workflow-view-diagram-modal.zoom-controls.zoom-in',
		defaultMessage: 'Zoom in',
		description: 'Label for workflow editor zoom in button',
	},

	zoomOut: {
		id: 'workflow-view-diagram-modal.zoom-controls.zoom-out',
		defaultMessage: 'Zoom out',
		description: 'Label for workflow editor zoom out button',
	},

	zoomSlider: {
		id: 'workflow-view-diagram-modal.zoom-controls.zoom-slider',
		defaultMessage: 'Zoom level',
		description: 'Label for workflow editor zoom range slider',
	},
});

type Props = {
	onZoomChange: (zoom: number) => void;
	zoom: number;
};

export const ZoomControls = ({ onZoomChange, zoom }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const changeZoom = useCallback(
		(linearZoom: number) => {
			const exponentialZoom = getExponentialZoom(linearZoom);
			onZoomChange(exponentialZoom);
		},
		[onZoomChange],
	);

	const onZoomIn = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const linearZoom = getLinearZoom(zoom) + LINEAR_ZOOM_STEP;
			changeZoom(linearZoom);
			fireUIAnalytics(analyticsEvent, 'zoomIn');
		},
		[changeZoom, zoom],
	);

	const onZoomOut = useCallback(
		(e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const linearZoom = getLinearZoom(zoom) - LINEAR_ZOOM_STEP;
			changeZoom(linearZoom);
			fireUIAnalytics(analyticsEvent, 'zoomOut');
		},
		[changeZoom, zoom],
	);
	return (
		<Box xcss={zoomControlContainerStyles}>
			<Box xcss={zoomControlsStyles}>
				<Button
					isDisabled={isMinimumZoom(getLinearZoom(zoom))}
					testId="workflow-view-diagram-modal.zoom-out-button"
					appearance="subtle"
					iconBefore={
						<MediaServicesZoomOutIcon spacing="spacious" color={token('color.icon')} label="" />
					}
					onClick={onZoomOut}
					aria-label={formatMessage(messages.zoomOut)}
					interactionName="auto-generated.workflow-readonly-modal.ui.diagram.zoom-out-button.button"
				/>
				<Range
					min={MINIMUM_LINEAR_ZOOM}
					max={MAXIMUM_LINEAR_ZOOM}
					step={LINEAR_ZOOM_STEP}
					value={getLinearZoom(zoom)}
					onChange={changeZoom}
					aria-label={formatMessage(messages.zoomSlider)}
				/>
				<Button
					testId="workflow-view-diagram-modal.zoom-in-button"
					isDisabled={isMaximumZoom(getLinearZoom(zoom))}
					appearance="subtle"
					iconBefore={
						<MediaServicesZoomInIcon spacing="spacious" color={token('color.icon')} label="" />
					}
					onClick={onZoomIn}
					aria-label={formatMessage(messages.zoomIn)}
					interactionName="auto-generated.workflow-readonly-modal.ui.diagram.zoom-in-button.button1"
				/>
			</Box>
		</Box>
	);
};

const zoomControlContainerStyles = xcss({
	position: 'relative',
});

const zoomControlsStyles = xcss({
	position: 'absolute',
	bottom: 'space.100',
	right: 'space.100',
	width: '200px',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',

	backgroundColor: 'color.background.neutral',
	height: '40px',
	justifyContent: 'space-evenly',
	paddingTop: 'space.0',
	paddingRight: 'space.050',
	paddingBottom: 'space.0',
	paddingLeft: 'space.050',
});
